import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Typography, Space, Select, Skeleton, Spin, Alert, Breadcrumb, Layout, Tabs, theme } from 'antd';
import { LineChartOutlined, PartitionOutlined, ShopOutlined } from '@ant-design/icons';
import { useIntl } from 'react-intl';
import { BrandAnalyticsComponent } from '../components/BrandAnalytics';
import { SegmentComparisonTab } from '../components/SegmentComparison/SegmentComparisonTab';
import { LockedSegmentComparisonPreview } from '../components/SegmentComparison/LockedSegmentComparisonPreview';
import { useGetOrgBrandsQuery, useGetOrganisationEntitlementsQuery } from '../services/nestjs-base-api';
import { useAuth } from '../hooks/useAuth';
import { MaturityTag } from '../components/MaturityTag';
import { MaturityAlert } from '../components/MaturityAlert';

const SEGMENT_COMPARISON_ACCESS = 'SEGMENT_COMPARISON_ACCESS';
const SEGMENT_COMPARISON_MAX_SERIES = 'SEGMENT_COMPARISON_MAX_SERIES';

const { Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;

export const BrandTrendsHistoryPage: React.FC = () => {
    const intl = useIntl();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const {
        token: { colorBgContainer, borderRadiusLG },
    } = theme.useToken();

    const [selectedBrandHash, setSelectedBrandHash] = useState<string | null>(
        searchParams.get('brandHash')
    );

    // Get organization ID from user
    const orgId = user?.organisation?.id?.toString();

    // Fetch brands for the organization
    const {
        data: brands,
        isLoading: brandsLoading,
        error: brandsError
    } = useGetOrgBrandsQuery(
        {
            jwtToken: user?.jwtToken || '',
            orgId: orgId || ''
        },
        { skip: !orgId || !user?.jwtToken }
    );

    // Auto-select brand if only one is available
    useEffect(() => {
        if (brands && brands.length === 1 && !selectedBrandHash) {
            const brandHash = brands[0].hash;
            setSelectedBrandHash(brandHash);
            const newParams = new URLSearchParams(searchParams);
            newParams.set('brandHash', brandHash);
            newParams.set('brandName', brands[0].name);
            setSearchParams(newParams);
        }
    }, [brands, selectedBrandHash, searchParams, setSearchParams]);

    const handleBrandChange = (brandHash: string) => {
        const brand = brands?.find(b => b.hash === brandHash);
        setSelectedBrandHash(brandHash);
        const newParams = new URLSearchParams(searchParams);
        newParams.set('brandHash', brandHash);
        if (brand) {
            newParams.set('brandName', brand.name);
        }
        setSearchParams(newParams);
    };

    const selectedBrand = brands?.find(b => b.hash === selectedBrandHash);
    const brandName = searchParams.get('brandName') || selectedBrand?.name || 'Unknown Brand';

    if (brandsLoading) {
        return (
            <Content style={{ margin: '0 16px' }}>
                <Breadcrumb
                    style={{ margin: '16px 0' }}
                    items={[
                        { title: user?.organisation?.name || 'Organization' },
                        { title: intl.formatMessage({ id: 'trends.breadcrumb.analytics' }) },
                        { title: intl.formatMessage({ id: 'trends.breadcrumb.trends' }) }
                    ]}
                />
                <div
                    style={{
                        padding: 24,
                        minHeight: '82vh',
                        background: colorBgContainer,
                        borderRadius: borderRadiusLG,
                    }}
                >
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '400px'
                    }}>
                        <div style={{ textAlign: 'center' }}>
                            <Spin size="large" />
                            <div style={{ fontSize: '16px', marginTop: '16px' }}>{intl.formatMessage({ id: 'trends.loadingBrands' })}</div>
                        </div>
                    </div>
                </div>
            </Content>
        );
    }

    if (brandsError || !orgId) {
        return (
            <Content style={{ margin: '0 16px' }}>
                <Breadcrumb
                    style={{ margin: '16px 0' }}
                    items={[
                        { title: user?.organisation?.name || 'Organization' },
                        { title: intl.formatMessage({ id: 'trends.breadcrumb.analytics' }) },
                        { title: intl.formatMessage({ id: 'trends.breadcrumb.trends' }) }
                    ]}
                />
                <div
                    style={{
                        padding: 24,
                        minHeight: '82vh',
                        background: colorBgContainer,
                        borderRadius: borderRadiusLG,
                    }}
                >
                    <Alert
                        message={intl.formatMessage({ id: 'trends.errorLoadingBrands.title' })}
                        description={intl.formatMessage({ id: 'trends.errorLoadingBrands.description' })}
                        type="error"
                        showIcon
                    />
                </div>
            </Content>
        );
    }

    if (!brands || brands.length === 0) {
        return (
            <Content style={{ margin: '0 16px' }}>
                <Breadcrumb
                    style={{ margin: '16px 0' }}
                    items={[
                        { title: user?.organisation?.name || 'Organization' },
                        { title: intl.formatMessage({ id: 'trends.breadcrumb.analytics' }) },
                        { title: intl.formatMessage({ id: 'trends.breadcrumb.trends' }) }
                    ]}
                />
                <div
                    style={{
                        padding: 24,
                        minHeight: '82vh',
                        background: colorBgContainer,
                        borderRadius: borderRadiusLG,
                    }}
                >
                    <Alert
                        message={intl.formatMessage({ id: 'trends.noData.title' })}
                        description={
                            <div>
                                <p>{intl.formatMessage({ id: 'trends.noData.description1' })}</p>
                                <p>{intl.formatMessage({ id: 'trends.noData.description2' })}</p>
                                <Button
                                    type="primary"
                                    onClick={() => navigate('/dashboard')}
                                    style={{ marginTop: '8px' }}
                                >
                                    {intl.formatMessage({ id: 'trends.backToDashboard' })}
                                </Button>
                            </div>
                        }
                        type="info"
                        showIcon
                    />
                </div>
            </Content>
        );
    }

    if (!selectedBrandHash) {
        return (
            <Content style={{ margin: '0 16px' }}>
                <Breadcrumb
                    style={{ margin: '16px 0' }}
                    items={[
                        { title: user?.organisation?.name || 'Organization' },
                        { title: intl.formatMessage({ id: 'trends.breadcrumb.analytics' }) },
                        { title: intl.formatMessage({ id: 'trends.breadcrumb.trends' }) }
                    ]}
                />
                <div
                    style={{
                        padding: 24,
                        minHeight: '82vh',
                        background: colorBgContainer,
                        borderRadius: borderRadiusLG,
                    }}
                >
                    <Title level={2} style={{ margin: 0, marginBottom: '8px' }}>
                        {intl.formatMessage({ id: 'trends.title' })}
                        <MaturityTag type="earlyAccess" />
                    </Title>
                    <Text style={{ color: '#6b7280', fontSize: '16px', display: 'block', marginBottom: 16 }}>
                        {intl.formatMessage({ id: 'trends.subtitle' })}
                    </Text>
                    <MaturityAlert type="earlyAccess" style={{ marginBottom: 24 }} />

                    <Alert
                        message={intl.formatMessage({ id: 'trends.selectBrand.title' })}
                        description={
                            <div>
                                <p>{intl.formatMessage({ id: 'trends.selectBrand.description' })}</p>
                                <Select
                                    placeholder={intl.formatMessage({ id: 'trends.selectBrand.placeholder' })}
                                    style={{ width: '100%', maxWidth: 400, marginTop: 16 }}
                                    size="large"
                                    onChange={handleBrandChange}
                                >
                                    {brands.map(brand => (
                                        <Option key={brand.hash} value={brand.hash}>
                                            <Space>
                                                <ShopOutlined />
                                                {brand.name}
                                            </Space>
                                        </Option>
                                    ))}
                                </Select>
                            </div>
                        }
                        type="info"
                        showIcon
                    />
                </div>
            </Content>
        );
    }

    return (
        <Content style={{ margin: '0 16px' }}>
            <Breadcrumb
                style={{ margin: '16px 0' }}
                items={[
                    { title: user?.organisation?.name || 'Organization' },
                    { title: intl.formatMessage({ id: 'trends.breadcrumb.analytics' }) },
                    { title: intl.formatMessage({ id: 'trends.breadcrumb.trends' }) }
                ]}
            />

            <div
                style={{
                    padding: 24,
                    minHeight: '82vh',
                    background: colorBgContainer,
                    borderRadius: borderRadiusLG,
                }}
            >
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
                    <div>
                        <Title level={2} style={{ margin: 0, marginBottom: '8px' }}>
                            {intl.formatMessage({ id: 'trends.title' })}
                            <MaturityTag type="earlyAccess" />
                        </Title>
                        <Text style={{ color: '#6b7280', fontSize: '16px' }}>
                            {intl.formatMessage({ id: 'trends.subtitle' })}
                        </Text>
                    </div>

                    {/* Brand Switcher for Multi-brand Organizations */}
                    {brands.length > 1 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Text strong>{intl.formatMessage({ id: 'trends.brandLabel' })}</Text>
                            <Select
                                value={selectedBrandHash}
                                onChange={handleBrandChange}
                                style={{ minWidth: 200 }}
                            >
                                {brands.map(brand => (
                                    <Option key={brand.hash} value={brand.hash}>
                                        <Space>
                                            <ShopOutlined />
                                            {brand.name}
                                        </Space>
                                    </Option>
                                ))}
                            </Select>
                        </div>
                    )}
                </div>
                <MaturityAlert type="earlyAccess" />

                {/* Tabs: Overview + Segment Comparison */}
                <TrendsTabs
                    brandHash={selectedBrandHash}
                    brandName={brandName}
                    orgId={orgId}
                />
            </div>
        </Content>
    );
};

export interface TrendsTabsProps {
    brandHash: string;
    brandName: string;
    orgId: string;
    /** Controlled period (`{N}_days` / `all_time`). When set, the Overview
     *  hides its own period selector and follows the global Marketer View scope
     *  so Brand Equity matches the other tabs (SYR-68). */
    period?: string;
}

/** Overview + Segment Comparison sub-tabs (with entitlement gating). Exported
 *  for reuse in the Marketer View "The Benchmarks → Trends" tab (SYR-68). */
export const TrendsTabs: React.FC<TrendsTabsProps> = ({ brandHash, brandName, orgId, period }) => {
    const intl = useIntl();
    const { user } = useAuth();
    const jwtToken = user?.jwtToken ?? '';
    const [searchParams, setSearchParams] = useSearchParams();
    const activeTab = searchParams.get('tab') === 'compare' ? 'compare' : 'overview';

    const { data: entitlements, isLoading: entitlementsLoading } = useGetOrganisationEntitlementsQuery(
        { jwtToken, orgId },
        { skip: !jwtToken || !orgId },
    );

    const { canCompareOnTrends, maxSeries } = useMemo(() => {
        const ent = entitlements ?? [];
        const access = ent.find((e) => e.feature === SEGMENT_COMPARISON_ACCESS);
        const maxRow = ent.find((e) => e.feature === SEGMENT_COMPARISON_MAX_SERIES);
        return {
            canCompareOnTrends: !!access?.hasAccess,
            maxSeries: maxRow?.limit ?? 0,
        };
    }, [entitlements]);

    // Don't default to "locked" while entitlements are still loading —
    // render a skeleton so entitled users don't see the upgrade card flash
    // on every page reload.
    const renderCompareTabContent = () => {
        if (entitlementsLoading || !entitlements) {
            return (
                <div style={{ padding: 32 }}>
                    <Skeleton active paragraph={{ rows: 8 }} />
                </div>
            );
        }
        if (canCompareOnTrends) {
            return (
                <SegmentComparisonTab
                    brandHash={brandHash}
                    brandName={brandName}
                    maxSeries={Math.max(maxSeries, 1)}
                />
            );
        }
        return <LockedSegmentComparisonPreview />;
    };

    const handleTabChange = (key: string) => {
        const next = new URLSearchParams(searchParams);
        if (key === 'compare') next.set('tab', 'compare');
        else next.delete('tab');
        setSearchParams(next, { replace: true });
    };

    return (
        <Tabs
            activeKey={activeTab}
            onChange={handleTabChange}
            items={[
                {
                    key: 'overview',
                    label: (
                        <span>
                            <LineChartOutlined style={{ marginRight: 8 }} />
                            {intl.formatMessage({ id: 'trends.tabs.overview', defaultMessage: 'Overview' })}
                        </span>
                    ),
                    children: (
                        <BrandAnalyticsComponent
                            brandHash={brandHash}
                            orgId={orgId}
                            brandName={brandName}
                            periodOverride={period}
                            hidePeriodSelector={!!period}
                        />
                    ),
                },
                {
                    key: 'compare',
                    label: (
                        <span>
                            <PartitionOutlined style={{ marginRight: 8 }} />
                            {intl.formatMessage({ id: 'trends.tabs.segmentComparison', defaultMessage: 'Segment Comparison' })}
                            {!entitlementsLoading && entitlements && !canCompareOnTrends && (
                                <span style={{ marginLeft: 8, fontSize: 10, padding: '1px 6px', background: 'rgba(255,130,92,0.12)', color: '#ff825c', borderRadius: 8, fontWeight: 600, letterSpacing: 0.4 }}>
                                    PRO
                                </span>
                            )}
                        </span>
                    ),
                    children: renderCompareTabContent(),
                },
            ]}
        />
    );
};

export default BrandTrendsHistoryPage;
